import { KeyboardEvent, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { UsuarioAdmin } from '../../features/auth/model/types';
import { AndamentoPainel } from '../../features/andamento/ui/AndamentoPainel';
import { ClassificacaoPainel } from '../../features/classificacao/ui/ClassificacaoPainel';
import { BossFinalPainel } from '../../features/bossfinal/ui/BossFinalPainel';
import { CadastroPainel } from '../../features/cadastro/ui/CadastroPainel';
import { ConfiguracaoDaFase, FASES_DE_CONFIGURACAO } from '../../features/configuracoes/ui/ConfiguracaoDaFase';
import type { FaseDeConfiguracao } from '../../features/configuracoes/ui/ConfiguracaoDaFase';
import { VariaveisPainel } from '../../features/conteudo/ui/VariaveisPainel';
import { JogadoresPainel } from '../../features/jogadores/ui/JogadoresPainel';
import { LogPainel } from '../../features/log/ui/LogPainel';
import { MateriaisPainel } from '../../features/materiais/ui/MateriaisPainel';
import { OcorrenciasPainel } from '../../features/ocorrencias/ui/OcorrenciasPainel';
import { OperacaoPainel } from '../../features/operacao/ui/OperacaoPainel';
import { ParticipantesPainel } from '../../features/participantes/ui/ParticipantesPainel';
import { PontuacaoPainel } from '../../features/pontuacao/ui/PontuacaoPainel';
import { RankingPainel } from '../../features/ranking/ui/RankingPainel';
import { PaginaComHistorico } from '../../features/configuracoes/ui/PaginaComHistorico';
import { TextosConfiguracao } from '../../features/configuracoes/ui/TextosConfiguracao';

interface InicioPageProps {
    usuario: UsuarioAdmin;
    csrfToken: string;
    aoSair: () => Promise<void>;
}

type SecaoId =
    | 'operacao' | 'bossfinal' | 'andamento' | 'participantes' | 'jogadores' | 'cadastro'
    | 'variaveis' | 'pontuacao' | 'materiais' | 'textos' | 'ranking' | 'classificacao' | 'ocorrencias' | 'log'
    | FaseDeConfiguracao;

const ROTULO_DA_SECAO: Record<SecaoId, string> = {
    operacao: 'Operação',
    bossfinal: 'Boss Final',
    andamento: 'Andamento',
    participantes: 'Participantes',
    jogadores: 'Jogadores',
    cadastro: 'Cadastro',
    variaveis: 'Variáveis',
    pontuacao: 'Pontuação',
    materiais: 'Materiais',
    textos: 'Textos',
    ranking: 'Ranking',
    classificacao: 'Ranking',
    ocorrencias: 'Ocorrências',
    log: 'Log Técnico',
    ...(Object.fromEntries(FASES_DE_CONFIGURACAO.map(({ id, rotulo }) => [id, rotulo])) as Record<FaseDeConfiguracao, string>),
};

const ehFaseDeConfiguracao = (id: SecaoId): id is FaseDeConfiguracao =>
    FASES_DE_CONFIGURACAO.some((fase) => fase.id === id);

const svg = (conteudo: ReactNode) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {conteudo}
    </svg>
);

/**
 * O menu lateral do modelo (`docs/img_refac/img.png`, `RA1`). **Transição:** enquanto a tela nova
 * de um item não existe, ele mostra os painéis atuais em abas, para nada sumir (`RN-RA-005`).
 */
const ITENS = [
    {
        id: 'operacao',
        rotulo: 'Operação',
        descricao: 'Acompanhe grupos, atividades e ocorrências da turma em tempo real',
        icone: svg(<path d="M3 12h4l3-8 4 16 3-8h4" />),
        papeis: ['autou', 'fiat'],
        // Só a tela nova. O Andamento saiu daqui, mas o painel e as rotas continuam para uso futuro.
        secoes: ['operacao'],
    },
    {
        id: 'ranking',
        rotulo: 'Ranking',
        descricao: 'Acompanhe a classificação de grupos e de jogadores da turma',
        icone: svg(<path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" />),
        papeis: ['autou', 'fiat'],
        secoes: ['classificacao'],
    },
    {
        id: 'bossfinal',
        rotulo: 'Boss Final',
        descricao: 'Dispare o Boss Final da turma e exiba a animação final neste painel',
        icone: svg(<path d="M5 17h14l-1.5-5.5A2 2 0 0 0 15.6 10H8.4a2 2 0 0 0-1.9 1.5L5 17zM7 17v2M17 17v2M8 14h.01M16 14h.01" />),
        papeis: ['autou', 'fiat'],
        secoes: ['bossfinal'],
    },
    {
        id: 'participantes',
        rotulo: 'Participantes',
        descricao: 'Gerencie participantes, liderança, tablets e acesso',
        icone: svg(<><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5M16 11a3 3 0 1 0 0-6M21 20c0-2-1.5-3.6-4-4.4" /></>),
        papeis: ['autou', 'fiat'],
        // Só a tela nova. Jogadores e Cadastro saíram daqui; os painéis e as rotas continuam.
        secoes: ['participantes'],
    },
    {
        id: 'configuracoes',
        rotulo: 'Configurações',
        descricao: 'Gerencie textos, códigos, gabaritos e parâmetros globais do jogo',
        icone: svg(<path d="M4 7h10M18 7h2M4 17h4M12 17h8M14 4v6M8 14v6" />),
        papeis: ['autou', 'fiat'],
        // Uma aba por fase (variáveis, pontuação e, no Scan Pixel, materiais) e Textos. Os painéis
        // antigos (Variáveis, Pontuação, Materiais, Ranking) saíram da tela; código e rotas continuam.
        secoes: ['scanpixel', 'menuresolucao', 'labpixels', 'versus', 'player1', 'textos'],
    },
    {
        id: 'ocorrencias',
        rotulo: 'Ocorrências',
        descricao: 'Acompanhe ocorrências, erros, conexões e eventos da plataforma',
        icone: svg(<><circle cx="12" cy="12" r="2" /><path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7M5.6 18.4a9 9 0 0 1 0-12.8M18.4 5.6a9 9 0 0 1 0 12.8" /></>),
        // O backend recusa o Log para `fiat` (403); aqui o item simplesmente não existe.
        papeis: ['autou'],
        secoes: ['ocorrencias', 'log'],
    },
] as const satisfies readonly {
    id: string;
    rotulo: string;
    descricao: string;
    icone: ReactNode;
    papeis: readonly string[];
    secoes: readonly SecaoId[];
}[];

type Item = (typeof ITENS)[number];
type ItemId = Item['id'];

export function InicioPage({ usuario, csrfToken, aoSair }: InicioPageProps) {
    const [erroLogout, setErroLogout] = useState(false);
    const [itemAtivo, setItemAtivo] = useState<ItemId>('operacao');
    const [secaoDoItem, setSecaoDoItem] = useState<Partial<Record<ItemId, SecaoId>>>({});
    // Vem da carta do Andamento: abre Participantes > Jogadores já filtrado.
    const [filtroDeJogadores, setFiltroDeJogadores] = useState<{ turma: string; grupo: string } | null>(null);
    const abas = useRef<Partial<Record<SecaoId, HTMLButtonElement | null>>>({});

    const itens = ITENS.filter((item) => (item.papeis as readonly string[]).includes(usuario.papel));
    const item: Item = itens.find((candidato) => candidato.id === itemAtivo) ?? itens[0];
    const secoes = item.secoes as readonly SecaoId[];
    const secao = secaoDoItem[item.id] ?? secoes[0];

    async function encerrarSessao() {
        setErroLogout(false);
        try {
            await aoSair();
        } catch {
            setErroLogout(true);
        }
    }

    function abrirSecao(id: SecaoId, foco = true) {
        setSecaoDoItem((atual) => ({ ...atual, [item.id]: id }));
        if (foco) abas.current[id]?.focus();
    }

    function navegarComTeclado(evento: KeyboardEvent<HTMLDivElement>) {
        const atual = secoes.indexOf(secao);
        const destino = {
            ArrowRight: (atual + 1) % secoes.length,
            ArrowLeft: (atual - 1 + secoes.length) % secoes.length,
            Home: 0,
            End: secoes.length - 1,
        }[evento.key];
        if (destino === undefined) return;
        evento.preventDefault();
        abrirSecao(secoes[destino]);
    }

    function painel(id: SecaoId) {
        // `key`: trocar de fase remonta o painel, sem carregar o estado da fase anterior.
        if (ehFaseDeConfiguracao(id)) {
            return (
                <PaginaComHistorico key={id} pagina={id}>
                    <ConfiguracaoDaFase fase={id} csrfToken={csrfToken} />
                </PaginaComHistorico>
            );
        }
        switch (id) {
            case 'operacao':
                return <OperacaoPainel csrfToken={csrfToken} />;
            case 'bossfinal':
                return <BossFinalPainel csrfToken={csrfToken} />;
            case 'andamento':
                return (
                    <AndamentoPainel
                        aoVerJogadores={(turma, grupo) => {
                            setFiltroDeJogadores({ turma, grupo });
                            setItemAtivo('participantes');
                            setSecaoDoItem((atual) => ({ ...atual, participantes: 'jogadores' }));
                        }}
                        csrfToken={csrfToken}
                    />
                );
            case 'participantes':
                return <ParticipantesPainel csrfToken={csrfToken} />;
            case 'jogadores':
                return (
                    <JogadoresPainel
                        turmaInicial={filtroDeJogadores?.turma}
                        grupoInicial={filtroDeJogadores?.grupo}
                        csrfToken={csrfToken}
                    />
                );
            case 'cadastro':
                return <CadastroPainel csrfToken={csrfToken} />;
            case 'variaveis':
                return <VariaveisPainel csrfToken={csrfToken} />;
            case 'pontuacao':
                return <PontuacaoPainel csrfToken={csrfToken} />;
            case 'materiais':
                return <MateriaisPainel csrfToken={csrfToken} />;
            case 'textos':
                // A tela no padrão das fases; o `TextosPainel` antigo fica no código, fora da tela.
                return (
                    <PaginaComHistorico pagina="textos">
                        <TextosConfiguracao csrfToken={csrfToken} />
                    </PaginaComHistorico>
                );
            case 'ranking':
                return <RankingPainel csrfToken={csrfToken} />;
            case 'classificacao':
                return <ClassificacaoPainel csrfToken={csrfToken} />;
            case 'ocorrencias':
                return <OcorrenciasPainel />;
            case 'log':
                return <LogPainel />;
        }
    }

    return (
        <div className="painel-casca">
            <nav className="menu-lateral" aria-label="Menu do painel">
                <div className="marca-painel">
                    <p className="marca-nome"><span className="marca-barras" aria-hidden="true">////</span><span>FIAT ARCADE</span></p>
                    <p className="marca-subtitulo">PAINEL ADMINISTRATIVO</p>
                </div>
                <ul className="itens-menu">
                    {itens.map((candidato) => (
                        <li key={candidato.id}>
                            <button
                                type="button"
                                className="item-menu"
                                aria-current={candidato.id === item.id ? 'page' : undefined}
                                onClick={() => setItemAtivo(candidato.id)}
                            >
                                {candidato.icone}
                                <span>{candidato.rotulo}</span>
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="usuario-menu">
                    <span className="usuario-nome">{usuario.nome}</span>
                    <button className="botao-sair-menu" type="button" onClick={() => void encerrarSessao()}>
                        {svg(<><path d="M10 17l5-5-5-5M15 12H3" /><path d="M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5" /></>)}
                        <span className="somente-leitor">Sair</span>
                    </button>
                    {erroLogout && <p className="aviso-logout" role="alert">A sessão ainda pode estar ativa.</p>}
                </div>
            </nav>

            <main className="area-conteudo">
                <p className="caminho-pagina">{item.icone}<span>{item.rotulo}</span></p>
                <div className="cabecalho-secao">
                    <div>
                        <h1>{item.rotulo}</h1>
                        <p>{item.descricao}</p>
                    </div>
                    {/* Espaço à direita do título para os controles da tela ativa. */}
                    <div id="acoes-da-secao" className="acoes-da-secao" />
                </div>

                {secoes.length > 1 && (
                    <div className="abas-painel" role="tablist" aria-label={`Seções de ${item.rotulo}`} onKeyDown={navegarComTeclado}>
                        {secoes.map((id) => (
                            <button
                                key={id}
                                ref={(elemento) => { abas.current[id] = elemento; }}
                                id={`aba-${id}`}
                                className="aba-painel"
                                type="button"
                                role="tab"
                                aria-selected={id === secao}
                                aria-controls={`painel-${id}`}
                                tabIndex={id === secao ? 0 : -1}
                                onClick={() => abrirSecao(id)}
                            >
                                {ROTULO_DA_SECAO[id]}
                            </button>
                        ))}
                    </div>
                )}

                {secoes.length > 1 ? (
                    <section className="conteudo-painel" id={`painel-${secao}`} role="tabpanel" aria-labelledby={`aba-${secao}`} tabIndex={0}>
                        {painel(secao)}
                    </section>
                ) : (
                    <section className="conteudo-painel" aria-label={item.rotulo}>
                        {painel(secao)}
                    </section>
                )}
            </main>
        </div>
    );
}
